import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IRealtyValuationData, IRealtyValuationState, ITabs } from '../../types/realtyValuation';
import { IPricesFiltersSchema } from '../../repositories/newbuilding-estimate/entities/valuation_filters/PricesFiltersSchema';
import { ISalesFiltersSchema } from '../../repositories/newbuilding-estimate/entities/valuation_filters/SalesFiltersSchema';
import { EValuationTabs } from '../../containers/RealtyValuation/constants/tabs';

export const initialState: IRealtyValuationState = {
  isFetching: false,
  isError: false,
  data: null,
  pricesFiltersState: {} as IRealtyValuationState['pricesFiltersState'],
  salesFiltersState: {} as IRealtyValuationState['salesFiltersState'],
  tabs: null,
};

type TSuccessActionPayload = Partial<IRealtyValuationData>;

interface ISetPriceFilterPayload<Key extends keyof IPricesFiltersSchema> {
  filterName: Key;
  filterValue: IPricesFiltersSchema[Key];
}

interface ISetSalesFilterPayload<Key extends keyof ISalesFiltersSchema> {
  filterName: Key;
  filterValue: ISalesFiltersSchema[Key];
}

export const realtyValuationSlice = createSlice({
  name: 'realtyValuation',
  initialState,
  reducers: {
    requestAction: state => {
      state.isFetching = true;
    },
    failureAction: state => {
      state.isFetching = false;
      state.isError = true;
    },
    successAction: (state, action: PayloadAction<TSuccessActionPayload>) => {
      state.isFetching = false;
      state.isError = false;
      state.data = {
        ...(state.data as IRealtyValuationData),
        ...action.payload,
      };
    },
    setPriceFilterAction: (state, action: PayloadAction<ISetPriceFilterPayload<keyof IPricesFiltersSchema>>) => {
      const { filterName, filterValue } = action.payload;

      state.pricesFiltersState = {
        ...state.pricesFiltersState,
        [filterName]: filterValue,
      };
    },
    setSalesFilterAction: (state, action: PayloadAction<ISetSalesFilterPayload<keyof ISalesFiltersSchema>>) => {
      const { filterName, filterValue } = action.payload;

      state.salesFiltersState = {
        ...state.salesFiltersState,
        [filterName]: filterValue,
      };
    },
    setActiveTab: (state, action: PayloadAction<EValuationTabs>) => {
      state.tabs = {
        ...(state.tabs as ITabs),
        activeTab: action.payload,
      };
    },
  },
});

export const {
  reducer: realtyValuationReducer,
  actions: { requestAction, failureAction, successAction, setPriceFilterAction, setSalesFilterAction, setActiveTab },
} = realtyValuationSlice;
